// Single source of truth for the "an admin is impersonating a user" marker.
//
// The Supabase auth session lives in localStorage and is therefore shared by
// every open tab. Keeping the impersonation marker in sessionStorage meant a
// second tab silently became the impersonated user with no marker at all —
// rendering that user's popups and recording analytics against the wrong
// account. The marker must live in localStorage so all tabs agree.

const KEY = "podgo_impersonation";
const BANNER_HIDDEN_KEY = "podgo_impersonation_banner_hidden";

export interface ImpersonationData {
  adminAccessToken?: string;
  adminRefreshToken?: string;
  adminUserId?: string;
  targetUserId?: string;
  targetUserName?: string;
}

export function readImpersonation(): ImpersonationData | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ImpersonationData) : null;
  } catch {
    return null;
  }
}

export function writeImpersonation(data: ImpersonationData): void {
  // A fresh impersonation always shows the banner again.
  localStorage.removeItem(BANNER_HIDDEN_KEY);
  localStorage.setItem(KEY, JSON.stringify(data));
}

export function clearImpersonationStorage(): void {
  localStorage.removeItem(KEY);
  // The banner dismissal is scoped to one impersonation session, so it must
  // never survive into the admin's own session.
  localStorage.removeItem(BANNER_HIDDEN_KEY);
}

export function isBannerHidden(): boolean {
  return localStorage.getItem(BANNER_HIDDEN_KEY) === "true";
}

export function hideBanner(): void {
  localStorage.setItem(BANNER_HIDDEN_KEY, "true");
}

/** Fires in *other* tabs whenever the impersonation marker changes. */
export function onImpersonationChange(fn: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === null || e.key === KEY) fn();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
